import React, {useEffect, useRef, useState} from 'react';
import {
    CheckBox,
    Flex,
    Icon,
    Input,
    PriorityIcon,
    Text
} from '@priority-software/priority-style-react';
import styles from '../styles/item.module.scss';
import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import text from '../texts';
import {MenuActions, isUpword} from '../utils/submenu';
import {menuItemHeight} from '../consts';
import {Menu} from './Menu';
import {NewArea} from './NewArea';
import {ModernTooltip} from '../../../components/Common/Tooltip/ModernTooltip';
import texts from '../texts';
import {packageChange} from '../service';
import {useEventListener} from '../../../hooks/useEventListener';
import {keys} from '../../../constants/KeyCodes';
import {getLocalizedText, texts as legacyTexts, placeholders} from '../../../constants/TextConst';
import {showConfirmMessage} from '../../../_shared/Messages/actions/MessageActions';

export const Item = props => {
    const {
        item,
        pack,
        setSelectedItems,
        selectedItems,
        componentClasses,
        isFound,
        isViewItem,
        menuData,
        onUpdate,
        data,
        index,
        packages,
        setPackages,
        isMandatory = false,
        isInvalidDesign = false
    } = props;
    const itemRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const scrollIntoView = () => {
        //scrollIntoViewIfNeeded is not supported in Firefox
        itemRef.current?.scrollIntoViewIfNeeded
            ? itemRef.current?.scrollIntoViewIfNeeded()
            : itemRef.current?.scrollIntoView();
    };

    useEffect(() => {
        if (isFound) {
            scrollIntoView();
        }
    }, [isFound]);

    useEffect(() => {
        if (isViewItem) {
            scrollIntoView();
        }
    }, [isViewItem]);

    const onCheckboxChange = () => {
        //delete from array if already checked
        if (isChecked()) {
            setSelectedItems?.(selectedItems.filter(i => i != item));
        } else {
            setSelectedItems?.([...selectedItems, item]);
        }
    };

    const showHideHandler = (
        hideType: priority.netitems.form.Hide = priority.netitems.form.Hide.HiddenByDesign
    ) => {
        if (hideType === priority.netitems.form.Hide.HiddenByPackage) {
            updatePackagesStat(pack.IsPackageVisible);
        } else {
            updateHiddenStatus(hideType);
        }
    };

    const updatePackageStat = (item, pack) => {
        if (item.PackageId === pack.PackageId) {
            item.Hidden = pack.IsPackageVisible
                ? priority.netitems.form.Hide.Visible
                : priority.netitems.form.Hide.HiddenByPackage;
        }
    };

    const updatePackagesStat = async (isPackageVisible: boolean) => {
        const res = await packageChange(pack.PackageId, !isPackageVisible);
        const packagesByCategory = res.PackagesByCategory;
        const changedPackages = packagesByCategory.flatMap(category => category.Packages);
        displayDependentPacksConfirm(
            pack.Name,
            changedPackages.map(p => p.Name),
            `${!isPackageVisible ? texts.showPackage : texts.hidePackage} - ${pack?.Name}`,
            !isPackageVisible
                ? getLocalizedText(legacyTexts.showPackWarn)
                : getLocalizedText(legacyTexts.hidePackWarn),
            () => _updatePackagesStat(changedPackages)
        );
    };

    const displayDependentPacksConfirm = (
        activeFieldPackTitle,
        packagesTitles,
        title,
        msg,
        onConfirm
    ) => {
        let dependentPacks = '';
        for (const packTitle of packagesTitles) {
            dependentPacks = `${dependentPacks}${dependentPacks.length > 0 ? ',' : ''} ${
                packTitle.packtitle ? packTitle.packtitle : packTitle
            }`;
        }
        const _msg = msg
            ?.replace(placeholders.activeFieldPackTitle, activeFieldPackTitle)
            .replace(placeholders.dependentPacks, dependentPacks);

        const msgProps = {
            title: title,
            content: `${pack?.Description}\n${_msg}`,
            confirmButtonText: texts.yes,
            cancleButtonText: texts.no,
            defaultButton: 0,
            onConfirm,
            messageIcon: PriorityIcon.attention
        };

        showConfirmMessage(msgProps);
    };

    const _updatePackagesStat = changedPackages => {
        changedPackages.forEach(p => {
            data.forEach(tab => {
                if (tab.ColumnsDesign) {
                    tab.ColumnsDesign.forEach(i => {
                        updatePackageStat(i, p);
                    });
                } else {
                    updatePackageStat(tab, p);
                }
            });
        });

        const updatedPackages = packages.map(p => {
            const changed = changedPackages.find(c => p.PackageId === c.PackageId);
            if (changed) {
                p.IsPackageVisible = changed.IsPackageVisible;
                p.isDirty = true;
            }
            return p;
        });
        setPackages(updatedPackages);
        onUpdate();
    };

    const updateHiddenStatus = (hideType: priority.netitems.form.Hide) => {
        if (!item.Hidden && isChecked()) {
            onCheckboxChange();
        }
        const newHiddenStatus = item.Hidden ? priority.netitems.form.Hide.Visible : hideType;
        item.Hidden = newHiddenStatus;
        if (item.merged) {
            item.merged.Hidden = newHiddenStatus;
        }
        onUpdate();
    };

    const freezeHandler = () => {
        const currentFreezed = data.find(i => i.Cover);
        currentFreezed.Cover = false;
        item.Cover = true;
        onUpdate();
    };

    const toggleSpace = () => {
        item.Newarea = !item.Newarea;
        onUpdate();
    };

    const toggleMerge = () => {
        if (item.Merge) {
            item.Merge = false;
            onUpdate();
        }
    };

    const menuActionHandler = (action: MenuActions) => {
        switch (action) {
            case MenuActions.RENAME:
                setIsEditing(true);
                break;
            case MenuActions.FREEZE:
                freezeHandler();
                break;
            case MenuActions.HIDE:
                showHideHandler();
                break;
            case MenuActions.HIDE_PACKAGE:
                showHideHandler(priority.netitems.form.Hide.HiddenByPackage);
                break;
            case MenuActions.SPACE:
                toggleSpace();
                break;
            case MenuActions.MERGE:
                toggleMerge();
                break;
            default:
                break;
        }
    };

    const isChecked = () => {
        return selectedItems?.some(i => i.Name === item.Name);
    };

    const replaceTooltipPlaceholder = (text: string) => {
        return text.replace('X', item?.Title);
    };

    return (
        <div ref={itemRef} onMouseLeave={() => setIsMenuOpen(false)}>
            {item.Newarea && index !== 0 && <NewArea onClick={toggleSpace} />}
            <Flex
                justifyContent="space-between"
                componentClasses={cx(styles.item, componentClasses, {
                    [styles.hiddenItem]: item.Hidden,
                    [styles.foundItem]: isFound || isViewItem,
                    [styles.invalidDesign]: isInvalidDesign
                })}
            >
                <Flex justifyContent="flex-start">
                    <div
                        {...props.dragHandleProps}
                        className={cx(styles.dragIconContainer, {
                            [styles.draggedIconContainer]: props.isDragged
                        })}
                    >
                        <Icon icon={PriorityIcon.reorder} skin="standart" />
                    </div>

                    <CheckBox
                        checked={isChecked()}
                        onChange={onCheckboxChange}
                        disabled={item.Hidden}
                    />
                    {isEditing ? (
                        <RenameInput
                            onClose={() => setIsEditing(false)}
                            onUpdate={onUpdate}
                            item={item}
                        />
                    ) : (
                        <>
                            <Text skin="paragraph-2" componentClasses={styles.title}>
                                {`${item?.Title}${isMandatory ? '*' : ''}`}
                            </Text>

                            {item.Merge && (
                                <>
                                    <Icon
                                        icon={PriorityIcon.CellMerge}
                                        iconColor="secondary-500"
                                        componentClasses={styles.mergeIcon}
                                    />
                                    {
                                        <Text skin="paragraph-2" componentClasses={styles.title}>
                                            {item.merged?.Title}
                                        </Text>
                                    }
                                </>
                            )}
                            {isInvalidDesign && (
                                <ModernTooltip
                                    tooltip={
                                        isMandatory
                                            ? replaceTooltipPlaceholder(
                                                  texts.mandatoryFieldLimitWarn
                                              )
                                            : replaceTooltipPlaceholder(texts.fieldLimitWarn)
                                    }
                                    maxWidth={160}
                                    position="top"
                                >
                                    <Icon
                                        icon={PriorityIcon.moreInfo}
                                        iconColor="secondary-500"
                                        componentClasses={styles.invalidDesignIcon}
                                    />
                                </ModernTooltip>
                            )}
                        </>
                    )}
                </Flex>
                {!isEditing && (
                    <Flex justifyContent="flex-end">
                        {item.Cover && (
                            <Icon icon={PriorityIcon.pin} iconColor="primary-500"></Icon>
                        )}
                        {item.Hidden === priority.netitems.form.Hide.HiddenByPackage && (
                            <Icon icon={PriorityIcon.notDeployed} />
                        )}
                        <HoverPart
                            menuData={menuData}
                            isHidden={!!item.Hidden}
                            isMenuOpen={isMenuOpen}
                            setIsMenuOpen={setIsMenuOpen}
                            onShowHide={showHideHandler}
                            menuActionHandler={menuActionHandler}
                            isMandatory={isMandatory}
                            pack={pack}
                        />
                    </Flex>
                )}
            </Flex>
        </div>
    );
};

export const RenameInput = ({onClose, onUpdate, item, placeholder = null}) => {
    const [value, setValue] = useState(item.Title);
    const [value2, setValue2] = useState(item.merged?.Title);

    useEventListener('keydown', e => {
        if (e.key === keys.ENTER) {
            onSave();
        }
    });
    const onSave = () => {
        if (value?.length) {
            item.Title = value;
            if (item.merged) {
                item.merged.Title = value2;
            }
            onUpdate();
            onClose();
        }
    };

    return (
        <Flex>
            <Input
                autoFocus
                componentClasses={styles.renameInput}
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder={placeholder}
            />
            {item.merged && (
                <Input
                    componentClasses={styles.renameInput}
                    value={value2}
                    onChange={e => setValue2(e.target.value)}
                />
            )}
            <Text
                skin="link"
                textColor={value?.length ? 'primary-500' : 'grey-light'}
                onClick={onSave}
                componentClasses={styles.renameText}
            >
                {text.save}
            </Text>
            <Icon icon={PriorityIcon.closeSmall} iconColor="secondary-500" onClick={onClose} />
        </Flex>
    );
};

export const SimpleItem = ({item, menuData, onUpdate, pack}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const showHideHandler = () => {
        item.Hidden = item.Hidden
            ? priority.netitems.form.Hide.Visible
            : priority.netitems.form.Hide.HiddenByDesign;
        onUpdate();
    };
    return (
        <div onMouseLeave={() => setIsMenuOpen(false)} key={item.Name}>
            <Flex
                componentClasses={cx(styles.item, styles.simpleItem, {
                    [styles.hiddenItem]: item.Hidden
                })}
            >
                <Text skin="paragraph-2" wrap={false}>
                    {item?.Title}
                </Text>
                <HoverPart
                    isHidden={!!item.Hidden}
                    onShowHide={showHideHandler}
                    menuData={menuData}
                    isMenuOpen={isMenuOpen}
                    setIsMenuOpen={setIsMenuOpen}
                    pack={pack}
                />
            </Flex>
        </div>
    );
};

export const HoverPart = ({
    setIsMenuOpen,
    isMenuOpen,
    componentClasses = null,
    menuData,
    onShowHide,
    isHidden,
    menuActionHandler = null,
    isMandatory = false,
    pack = null
}) => {
    const [menuPosition, setMenuPosition] = useState(null);
    const triggerRef = useRef(null);

    const handleMenuOpen = (open: boolean) => {
        if (open && menuData) {
            if (isUpword(triggerRef, menuData.length)) {
                setMenuPosition('-' + (menuData.length * menuItemHeight).toString() + 'px');
            } else {
                const triggerHeight = 24;
                setMenuPosition('-' + triggerHeight.toString() + 'px');
            }
        }
        setIsMenuOpen(open);
    };

    return (
        <Flex componentClasses={cx(styles.hoverChild, componentClasses)} justifyContent="flex-end">
            {!isMandatory && (
                <>
                    <Icon
                        icon={isHidden ? PriorityIcon.showPasswordThin : PriorityIcon.passwordHide}
                        iconColor="primary-500"
                    />
                    <Text
                        skin="link"
                        textColor="primary-500"
                        onClick={() => onShowHide()}
                        componentClasses={styles.hideText}
                    >
                        {isHidden ? text.show : text.hide}
                    </Text>
                </>
            )}
            <div ref={triggerRef}>
                <Icon
                    componentClasses={cx({[styles.invisible]: !menuData || !menuData.length})}
                    icon={PriorityIcon.threeDots}
                    iconColor="secondary-500"
                    onClick={() => handleMenuOpen(!isMenuOpen)}
                />
            </div>
            {isMenuOpen && (
                <Menu
                    menuData={menuData}
                    menuStyle={{top: menuPosition}}
                    setIsMenuOpen={setIsMenuOpen}
                    menuActionHandler={menuActionHandler}
                    pack={pack}
                />
            )}
        </Flex>
    );
};
